/**
 * 大厅要打交道的几件环境事实：是不是微信内置浏览器、怎么复制文本、站点根地址、
 * 邀请链接带进来的房间码。
 *
 * 全部做成纯函数 + 可注入参数，既能单测，也能在没有 DOM 的地方（SSR / node 测试）安全调用。
 */
import { parseInviteTarget } from './roomCode.js'

/**
 * 微信内置浏览器（含企业微信）。
 *
 * 为什么要专门识别它（DEVICES.md R3）：微信会屏蔽域名，链接**随时可能打不开**，
 * 而且微信里装不了 PWA、Service Worker 受限。识别出来之后我们做两件事：
 *   1. 提示一次「点右上角 ··· → 在浏览器中打开」（文案见 `WECHAT_HINT_TEXT`）；
 *   2. 邀请面板里把「复制房间码」排在「复制链接」前面——6 个字符能穿过微信，链接不一定。
 */
export function isWechat(ua?: string): boolean {
  const s = ua ?? userAgent()
  return /MicroMessenger/i.test(s)
}

/**
 * 微信里唯一要说的那句话。
 *
 * ★ 只一句、也只说一次：以前大厅顶部、邀请面板、房间顶栏各写一版（「你正在微信里打开」
 *   「微信里先发房间码」「在浏览器中打开」），同一件事被说了三遍。
 */

/** iOS（含所有 WebView）。iOS 上 `navigator.vibrate` 恒不可用（R4），震动必须能力检测。 */
export function isIos(ua?: string): boolean {
  const s = ua ?? userAgent()
  return /iPad|iPhone|iPod/.test(s) || (/Macintosh/.test(s) && maxTouchPoints() > 1)
}

function userAgent(): string {
  return (globalThis as { navigator?: { userAgent?: string } }).navigator?.userAgent ?? ''
}

function maxTouchPoints(): number {
  return (globalThis as { navigator?: { maxTouchPoints?: number } }).navigator?.maxTouchPoints ?? 0
}

/** 站点根地址，用来拼邀请链接。没有 location（测试/SSR）时给一个明显是占位的值。 */
export function appOrigin(): string {
  return (globalThis as { location?: { origin?: string } }).location?.origin ?? 'http://localhost:5173'
}

/**
 * 复制文本。三级降级：
 *   1. `navigator.clipboard`（需要安全上下文，微信 WKWebView 里经常没有）；
 *   2. 隐藏 textarea + `execCommand('copy')`——老办法，但正是微信里唯一能用的那条；
 *   3. 都失败就返回 false，调用方必须**把码原样显示出来让用户长按选中**，
 *      绝不能只弹一句「复制失败」——那等于把人堵死在邀请这一步。
 */
export async function copyText(text: string): Promise<boolean> {
  const nav = (globalThis as { navigator?: { clipboard?: { writeText?(t: string): Promise<void> } } }).navigator
  const write = nav?.clipboard?.writeText
  if (write !== undefined) {
    try {
      await write.call(nav?.clipboard, text)
      return true
    } catch {
      // 落到 execCommand
    }
  }
  try {
    const doc = (globalThis as { document?: Document }).document
    if (doc?.body == null) return false
    const ta = doc.createElement('textarea')
    ta.value = text
    ta.setAttribute('readonly', '')
    // 必须在视口内且可聚焦，iOS 才肯把选区交给 execCommand；用 opacity 藏起来
    ta.style.cssText = 'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;border:0;padding:0'
    doc.body.appendChild(ta)
    ta.focus()
    ta.select()
    ta.setSelectionRange(0, text.length)
    const ok = doc.execCommand('copy')
    ta.remove()
    return ok
  } catch {
    return false
  }
}

/** 触觉反馈。★ iOS 恒不支持，必须能力检测后降级（R4），不能直接调。 */
export function vibrate(pattern: number | readonly number[]): void {
  const nav = (globalThis as { navigator?: { vibrate?: (p: number | number[]) => boolean } }).navigator
  if (nav?.vibrate === undefined) return
  try {
    nav.vibrate(typeof pattern === 'number' ? pattern : [...pattern])
  } catch {
    // 有的 WebView 会在无用户手势时抛，静默忽略
  }
}

// ============================================================================
// 邀请链接
// ============================================================================

export interface HistoryLike {
  replaceState(data: unknown, unused: string, url?: string): void
}

function currentHref(): string {
  return (globalThis as { location?: { href?: string } }).location?.href ?? ''
}

/**
 * 读一次邀请链接里的房间码（`/join?room=rm_x&code=QK37DF`），顺手把 URL 上的参数抹掉。
 *
 * ★ 必须「只消费一次」：不抹掉的话，玩家从牌桌退回大厅时地址栏里那个码还在，
 *   大厅会把他再一次推进同一个房间，形成退不出来的循环。
 * ★ 抹掉用 `replaceState` 而不是 `pushState`：不能往返回栈里塞一条假记录，
 *   否则按系统返回键会回到「带着码的那个地址」，又是一次自动进房。
 */
export function consumeInviteCode(
  href: string = currentHref(),
  history: HistoryLike | null = (globalThis as { history?: HistoryLike }).history ?? null,
): string | null {
  const { code } = parseInviteTarget(href)
  if (code === null) return null
  try {
    const url = new URL(href)
    history?.replaceState(null, '', url.pathname)
  } catch {
    // 没有 history（测试 / 老 WebView）就算了：大不了多消费一次
  }
  return code
}
